// MACHINERY V2 (Stroje) — the rendered spec sheet: toolbar (collapse-all +
// metric/imperial), description blocks, grouped sections (M2.8).
//
// >>> WHY THIS FILE EXISTS: THE §2.3 SPEC-STACK DEVIATION. <<<
// The M2.8 ticket row says the catalog detail should "reuse `MachineSpecs`".
// It must NOT. v1's components/MachineSpecs.tsx imports MUI at :2
// (`Box, CircularProgress, Tooltip, useMediaQuery`), THIRTEEN MUI icons at
// :3-16 and `dt` at :20 — pulling it into v2 would breach §3's styling law in
// one import. The instruction's PURPOSE was "do not reinvent spec display",
// and the MUI-free v2 spec stack (specResolve / specSheet / SpecSectionsV2 /
// specChrome / SpecDescription, all shipped at M2.5) satisfies that purpose
// better than its letter.
//
// This is the RENDER half of SpecificationsTabV2, lifted out verbatim so the
// profile tab and the catalog detail page draw the SAME sheet from the SAME
// code rather than two copies that drift. The tab keeps everything that is
// tab-specific — the de-duplicated fetch, the shared-slot id guard, the error
// state — and hands the resolved sheet here.
//
// STATE THAT MOVED WITH THE RENDER: `metric` and the `collapsed` exception set
// live here, because both are pure view state that only this markup reads.
// Sections start EXPANDED, so the set tracks the EXCEPTIONS, not the members —
// no effect, no initialisation race, and a newly arrived section is open like
// the rest. Callers reset it by remounting (`key={modelId}`), which is also how
// the tab used to reset it when the machine changed.
//
// EQUIPMENT SECTIONS KEEP THEIR OWN EXPAND STATE and are deliberately NOT
// driven by Expand-All (data package §3 "Actions"); that is `EquipmentSection`'s
// own `useState` and is unchanged by this extraction.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useState } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { ResolvedSpecSheet } from "./specResolve";
import { getSectionGroupKey } from "./specSheet";
import { GROUP_LABEL_KEYS, GroupDivider } from "./specChrome";
import { EquipmentSection, SpecSection } from "./SpecSectionsV2";
import { SpecDescription } from "./SpecDescription";

export const specSheetHasContent = (sheet: ResolvedSpecSheet): boolean =>
  sheet.primaryText.length > 0 ||
  sheet.machineTypeDescriptions.length > 0 ||
  sheet.regularSections.length > 0 ||
  sheet.equipmentSections.length > 0;

export interface SpecSheetViewProps {
  sheet: ResolvedSpecSheet;
  /** Mono eyebrow on the toolbar's left — the tab's intro line, or the
   *  catalog detail's "ŠPECIFIKÁCIE" label (artboard 0902). */
  label: string;
  /**
   * Rendered BETWEEN the toolbar and the sheet body: the retry notice, the
   * loading skeleton, the "no specifications" panel. A slot rather than props,
   * because the two hosts have genuinely different states to report (the tab
   * fetches into a SHARED slice slot and must guard the id; the catalog detail
   * owns its model outright) and neither should have to describe the other's.
   * The toolbar renders either way, so the metric toggle never disappears
   * under a transient state.
   */
  notices?: React.ReactNode;
  phone?: boolean;
}

const segment = (label: string, on: boolean, onClick: () => void) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    style={{
      fontFamily: "var(--font-mono)",
      fontSize: 10.5,
      padding: "3px 10px",
      borderRadius: 5,
      cursor: "pointer",
      background: on ? "var(--card)" : "transparent",
      color: on ? "var(--mod-accent)" : "var(--faint)",
      fontWeight: on ? 600 : 400,
      border: "1px solid " + (on ? "var(--border)" : "transparent"),
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </button>
);

const SpecSheetView: React.FC<SpecSheetViewProps> = ({ sheet, label, notices, phone }) => {
  const { translate } = useTranslation("myMachinery");
  const [metric, setMetric] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const hasContent = specSheetHasContent(sheet);

  const isOpen = (name: string) => !collapsed.has(name);
  const toggle = (name: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const allExpanded = collapsed.size === 0;
  const toggleAll = () =>
    setCollapsed(
      allExpanded ? new Set(sheet.regularSections.map((s) => s.sectionName)) : new Set()
    );

  /** One divider per group, at its first section — v1's `renderedGroups`. */
  const rendered = new Set<string>();
  const dividerFor = (sectionName: string) => {
    const key = getSectionGroupKey(sectionName);
    if (!key || rendered.has(key)) return null;
    rendered.add(key);
    return <GroupDivider label={translate(GROUP_LABEL_KEYS[key])} groupKey={key} />;
  };

  return (
    <React.Fragment>
      <div
        style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--faint)",
            letterSpacing: "0.04em",
            minWidth: 0,
          }}
        >
          {label}
        </span>
        <span style={{ flex: 1 }} />
        {hasContent && sheet.regularSections.length > 0 && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={toggleAll}
            style={{ height: 26, fontSize: 11.5, whiteSpace: "nowrap" }}
          >
            {translate(allExpanded ? "myMachinery:collapseAll" : "myMachinery:expandAll")}
          </button>
        )}
        <div
          style={{
            display: "inline-flex",
            gap: 2,
            padding: 2,
            background: "var(--bg-soft)",
            border: "1px solid var(--border)",
            borderRadius: 7,
          }}
        >
          {segment(translate("myMachinery:metric"), metric, () => setMetric(true))}
          {segment(translate("myMachinery:imperial"), !metric, () => setMetric(false))}
        </div>
      </div>

      {notices}

      {hasContent && (
        <React.Fragment>
          {sheet.primaryText && (
            <SpecDescription
              label={translate("myMachinery:specDescription")}
              text={sheet.primaryText}
              showEncodingFallback={sheet.usedEncodingFallback}
            />
          )}
          {sheet.machineTypeDescriptions.map((d) => (
            <SpecDescription key={d.label} label={d.label} text={d.text} />
          ))}

          {sheet.regularSections.map((section) => (
            <React.Fragment key={section.sectionName}>
              {dividerFor(section.sectionName)}
              <SpecSection
                section={section}
                open={isOpen(section.sectionName)}
                onToggle={() => toggle(section.sectionName)}
                toMetric={metric}
                phone={phone}
              />
            </React.Fragment>
          ))}

          {sheet.equipmentSections.length > 0 &&
            dividerFor(sheet.equipmentSections[0].sectionName)}
          {sheet.equipmentSections.map((section) => (
            <EquipmentSection key={section.sectionName} section={section} phone={phone} />
          ))}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default SpecSheetView;
